/**
 * Reading the runner registry.
 *
 * Two decisions live here rather than at the call sites, because both have a
 * failure mode that looks like ordinary emptiness:
 *
 *  1. How deep the response envelope is. `apiClient.get` already returns the
 *     response *body*, so `/admin/agent-runners` needs exactly one unwrap.
 *     Unwrapping twice yields `undefined`, and every consumer defaults that to
 *     an empty list — so the delegate dialog's repository picker showed only
 *     "From the agent's site" and the Infrastructure panel showed no runner,
 *     which is indistinguishable from a box that is genuinely offline.
 *
 *  2. Where a runner's repository list actually lives. The daemon reports it
 *     inside `host`, and the top-level `repositories` column is still null in
 *     production. A picker that reads only one of the two is empty whenever
 *     the runner happens to use the other.
 *
 * Both are the same shape of bug: a wrong answer that renders as "nothing to
 * show" instead of as an error. That is what the tests assert against — not
 * that the parse succeeds, but that a real captured payload produces the repos
 * a human would expect to see in the list.
 */
import type { AgentRunnerInfo } from "@/types/agent.types"

/** A runner as it arrives on the wire, before either shape decision is made. */
type RunnerWire = AgentRunnerInfo & {
  repositories?: string[] | null
  host?: { repositories?: string[] | null } | null
}

/**
 * Unwrap the `{data: [...]}` envelope exactly once. A body that is missing,
 * malformed, or already unwrapped yields `[]` rather than throwing: callers
 * render a list, and a thrown error inside a react-query selector would take
 * down the surrounding page over a cosmetic field.
 */
export function unwrapRunners(body: unknown): AgentRunnerInfo[] {
  if (Array.isArray(body)) return body as AgentRunnerInfo[]
  const data = (body as { data?: unknown } | null)?.data
  return Array.isArray(data) ? (data as AgentRunnerInfo[]) : []
}

/**
 * Every repository any runner reports it can reach, deduped and sorted — the
 * choices offered when delegating work.
 *
 * A non-empty top-level list wins over `host.repositories`; an empty or absent
 * one falls through. Empty is treated as "not reported" rather than as "this
 * runner can reach nothing on purpose", because the two are indistinguishable
 * on the wire and only one of them has a harmless failure: offering a repo
 * that turns out to be unreachable fails loudly at claim time, while hiding a
 * reachable repo leaves someone unable to delegate with no explanation.
 */
export function knownRepositories(runners: readonly AgentRunnerInfo[]): string[] {
  const all = (runners as readonly RunnerWire[]).flatMap((runner) => {
    const top = runner.repositories
    if (Array.isArray(top) && top.length > 0) return top
    return runner.host?.repositories ?? []
  })
  return [...new Set(all)].sort()
}
